import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Copy, Pencil, Trash2 } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  Dialog,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@rumbledirect/ui-v2";
import type {
  VehicleType,
  VehicleTypeServicePartner,
} from "@/configurator/vehicleTypes/vehicleType";
import { useCopyVehicleType } from "@/configurator/vehicleTypes/useCopyVehicleType";
import { useDealerOptions } from "@/configurator/vehicleTypes/useDealerOptions";
import { useDeleteVehicleType } from "@/configurator/vehicleTypes/useDeleteVehicleType";
import { useVehicleCategoryOptions } from "@/configurator/vehicleTypes/useVehicleCategoryOptions";
import {
  enumLabel,
  formatCentsAsEuros,
  groupVehicleTypeOptions,
  PICKUP_SERVICE_PARTNER_TYPE_LABELS,
  PRICE_CATEGORY_LABELS,
} from "@/configurator/vehicleTypes/vehicleTypeDisplay";
import { VehicleTypeMediaTab } from "@/configurator/vehicleTypes/VehicleTypeMediaTab";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminConfiguratorVehicleTypesId } from "@/lib/api/generated/configurator/configurator";
import {
  FUEL_TYPE_LABELS,
  toVehicleSpecificationDetailFields,
} from "@/vehicleSpecifications/vehicleSpecification";

const UPDATE_PERMISSION = "admin_configurator_vehicle_type_update";
const COPY_PERMISSION = "admin_configurator_vehicle_type_copy";
const DELETE_PERMISSION = "admin_configurator_vehicle_type_delete";

const DETAIL_TABS = ["details", "opties", "servicepartners", "media"] as const;
type DetailTab = (typeof DETAIL_TABS)[number];
const DEFAULT_TAB: DetailTab = "details";

function isDetailTab(value: unknown): value is DetailTab {
  return DETAIL_TABS.includes(value as DetailTab);
}

interface VehicleTypeShowSearch {
  readonly tab: DetailTab;
}

export const Route = createFileRoute(
  "/_authenticated/configurator/vehicle-types_/$vehicleTypeId",
)({
  validateSearch: (search: Record<string, unknown>): VehicleTypeShowSearch => ({
    tab: isDetailTab(search.tab) ? search.tab : DEFAULT_TAB,
  }),
  component: VehicleTypeShowPage,
});

function VehicleTypeShowPage(): ReactNode {
  const { vehicleTypeId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const routeNavigate = Route.useNavigate();
  const toast = useToast();

  const { data, isPending, isError, refetch } =
    useGetAdminConfiguratorVehicleTypesId(vehicleTypeId);

  const copyMutation = useCopyVehicleType();
  const deleteMutation = useDeleteVehicleType();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const title = data
    ? `${data.brand} ${data.model}`.trim() || "Voertuigtype"
    : "Voertuigtype";

  return (
    <div className="space-y-5">
      <Link
        to="/configurator/vehicle-types"
        search={{ page: 1, vehicleCategory: undefined }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Terug naar voertuigtypen
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        {isPending ? (
          <Skeleton className="h-8 w-64" />
        ) : (
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {data &&
              (data.active ? (
                <Badge variant="success" size="sm">
                  Actief
                </Badge>
              ) : (
                <Badge variant="neutral" size="sm">
                  Inactief
                </Badge>
              ))}
          </div>
        )}

        {data && (
          <div className="flex items-center gap-2">
            <RequirePermission permission={UPDATE_PERMISSION}>
              <Link
                to="/configurator/vehicle-types/$vehicleTypeId/edit"
                params={{ vehicleTypeId }}
                search={{ tab: "details" }}
                className={buttonVariants({ size: "sm" })}
              >
                <Pencil className="size-4" aria-hidden="true" />
                Bewerken
              </Link>
            </RequirePermission>
            <RequirePermission permission={COPY_PERMISSION}>
              <Button
                variant="outline"
                size="sm"
                disabled={copyMutation.isPending}
                onClick={() =>
                  copyMutation.mutate(vehicleTypeId, {
                    onSuccess: (created) => {
                      if (created.id)
                        void navigate({
                          to: "/configurator/vehicle-types/$vehicleTypeId",
                          params: { vehicleTypeId: created.id },
                          search: { tab: "details" },
                        });
                    },
                  })
                }
              >
                <Copy className="size-4" aria-hidden="true" />
                Dupliceren
              </Button>
            </RequirePermission>
            <RequirePermission permission={DELETE_PERMISSION}>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="size-4" aria-hidden="true" />
                Archiveren
              </Button>
            </RequirePermission>
          </div>
        )}
      </div>

      {isError ? (
        <Card className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="text-muted-foreground">
            We konden dit voertuigtype niet laden.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Opnieuw proberen
          </Button>
        </Card>
      ) : isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <VehicleTypeShowBody
          vehicleType={data}
          tab={tab}
          onTabChange={(nextTab) =>
            void routeNavigate({
              search: (prev) => ({ ...prev, tab: nextTab }),
            })
          }
        />
      )}

      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Voertuigtype archiveren"
        description={`Weet je zeker dat je "${title}" wilt archiveren? Deze actie kan niet ongedaan worden gemaakt.`}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              disabled={deleteMutation.isPending}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleteMutation.mutate(vehicleTypeId, {
                  onSuccess: () => {
                    setConfirmDelete(false);
                    void navigate({
                      to: "/configurator/vehicle-types",
                      search: { page: 1, vehicleCategory: undefined },
                    });
                  },
                  onError: () =>
                    toast.error("Archiveren is mislukt. Probeer het opnieuw."),
                })
              }
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Archiveren
            </Button>
          </>
        }
      />
    </div>
  );
}

function VehicleTypeShowBody({
  vehicleType,
  tab,
  onTabChange,
}: {
  readonly vehicleType: VehicleType;
  readonly tab: DetailTab;
  readonly onTabChange: (tab: DetailTab) => void;
}): ReactNode {
  const { options: categoryOptions } = useVehicleCategoryOptions();
  const {
    options: dealerOptions,
    isError: dealersFailed,
    refetch: refetchDealers,
  } = useDealerOptions();

  const categoryLabel = useMemo(() => {
    if (!vehicleType.vehicleCategory) return "—";
    return (
      categoryOptions.find(
        (option) => option.value === vehicleType.vehicleCategory,
      )?.label ?? vehicleType.vehicleCategory
    );
  }, [categoryOptions, vehicleType.vehicleCategory]);

  const dealerLabels = useMemo(() => {
    const dealers = vehicleType.dealers;
    if (dealers.length === 0) return "Geen dealers gekoppeld.";
    return dealers
      .map(
        (id) =>
          dealerOptions.find((option) => option.value === id)?.label ?? id,
      )
      .join(", ");
  }, [dealerOptions, vehicleType.dealers]);

  // A partial list would show a raw ID for every dealer on a page that failed,
  // so the failure is named instead of guessed around.
  const dealerValue = dealersFailed ? (
    <span className="inline-flex items-center gap-2" role="alert">
      <span className="text-muted-foreground">
        De dealers konden niet worden geladen.
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => refetchDealers()}
      >
        Opnieuw proberen
      </Button>
    </span>
  ) : (
    dealerLabels
  );

  const optionGroups = groupVehicleTypeOptions(vehicleType.options).filter(
    (group) => group.options.length > 0,
  );
  const servicePartners = [...vehicleType.servicePartners].sort(
    (a, b) => a.position - b.position,
  );

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => {
        if (isDetailTab(value)) onTabChange(value);
      }}
      className="flex flex-col gap-5"
    >
      <TabsList className="self-start">
        <TabsTrigger value="details">Details</TabsTrigger>
        <TabsTrigger value="opties">Opties</TabsTrigger>
        <TabsTrigger value="servicepartners">Servicepartners</TabsTrigger>
        <TabsTrigger value="media">Media</TabsTrigger>
      </TabsList>

      <TabsContent value="details">
        <Card className="space-y-4 p-6">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
            <DetailRow label="Voertuigcategorie" value={categoryLabel} />
            <DetailRow
              label="Prijs"
              value={formatCentsAsEuros(vehicleType.price)}
            />
            <DetailRow label="Kleur" value={vehicleType.color} />
            <DetailRow label="Bekleding" value={vehicleType.upholstery} />
            <DetailRow
              label="Prijscategorie"
              value={enumLabel(
                PRICE_CATEGORY_LABELS,
                vehicleType.priceCategory,
              )}
            />
            <DetailRow
              label="Ophaal-servicepartnertype"
              value={enumLabel(
                PICKUP_SERVICE_PARTNER_TYPE_LABELS,
                vehicleType.pickupServicePartnerType,
              )}
            />
            <DetailRow label="Dealers" value={dealerValue} />
          </dl>
          {vehicleType.particularities && (
            <DetailRow
              label="Bijzonderheden"
              value={vehicleType.particularities}
            />
          )}
        </Card>

        <Card className="mt-5 space-y-4 p-6">
          <h3 className="text-sm font-semibold text-foreground">
            Specificaties
          </h3>
          <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
            <DetailRow
              label="Brandstof"
              value={enumLabel(
                FUEL_TYPE_LABELS,
                vehicleType.specification?.fuelType,
              )}
            />
            {toVehicleSpecificationDetailFields(vehicleType.specification).map(
              (field) => (
                <DetailRow
                  key={field.label}
                  label={field.label}
                  value={field.value}
                />
              ),
            )}
          </dl>
        </Card>
      </TabsContent>

      <TabsContent value="opties">
        <Card className="space-y-5 p-6">
          {optionGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nog geen opties.</p>
          ) : (
            optionGroups.map((group) => (
              <div key={group.def.key} className="space-y-2">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  {group.def.label}
                </h3>
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {group.options.map((option) => (
                    <li
                      key={option.id}
                      className="flex items-center justify-between gap-4 px-4 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{option.title}</p>
                        {option.code && (
                          <p className="truncate text-xs text-muted-foreground">
                            {option.code}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {option.isAmount && (
                          <Badge variant="neutral" size="sm">
                            Aantal
                            {option.maxAmount
                              ? ` (max ${option.maxAmount})`
                              : ""}
                          </Badge>
                        )}
                        <span className="text-sm tabular-nums text-muted-foreground">
                          {formatCentsAsEuros(option.price)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </Card>
      </TabsContent>

      <TabsContent value="servicepartners">
        <Card className="p-6">
          <ServicePartnerList servicePartners={servicePartners} />
        </Card>
      </TabsContent>

      <TabsContent value="media">
        <Card className="p-6">
          <VehicleTypeMediaTab
            vehicleTypeId={vehicleType.id}
            media={vehicleType.media}
            canUpdate={false}
          />
        </Card>
      </TabsContent>
    </Tabs>
  );
}

function ServicePartnerList({
  servicePartners,
}: {
  readonly servicePartners: readonly VehicleTypeServicePartner[];
}): ReactNode {
  if (servicePartners.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nog geen servicepartners gekoppeld.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {servicePartners.map((partner) => (
        <li key={partner.id} className="px-4 py-2.5 font-medium">
          {partner.servicePartnerName}
        </li>
      ))}
    </ul>
  );
}

function DetailRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: ReactNode;
}): ReactNode {
  const isBlank =
    value == null || (typeof value === "string" && value.trim() === "");

  return (
    <div>
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">
        {isBlank ? "—" : value}
      </dd>
    </div>
  );
}
